import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, extractApiError } from "../lib/api";
import { toast } from "sonner";
import PageHeader from "../components/layout/PageHeader";
import Timeline from "../components/activity/Timeline";
import { ACTIVITY_TYPE_LABELS } from "../lib/labels";
import { ACTIVITY_TYPE } from "../lib/enums";
import {
  GLOBAL_ACTIVITY_PAGE_SIZE,
  mergeActivities,
  normalizeActivityPageResponse,
} from "../lib/activity-pagination";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { getActivitiesEmptyText, hasActiveActivitiesFilters } from "../lib/activity-filters";
import { useAuth } from "../lib/auth-context";
import { canDeleteActivity } from "../lib/crm-permissions";

const ALL_CONTEXTS = "__all__";
const ACTIVITY_CONTEXT_OPTIONS = [
  { value: ALL_CONTEXTS, label: "Alle Kontexte" },
  { value: "lead", label: "Leads" },
  { value: "deal", label: "Deals" },
  { value: "company", label: "Firmen" },
  { value: "contact", label: "Kontakte" },
  { value: "task", label: "Aufgaben" },
];

export default function ActivitiesPage() {
  const { role, isSuperAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState(null);
  const [contextFilter, setContextFilter] = useState(ALL_CONTEXTS);
  const [users, setUsers] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);

  const hasActiveFilters = hasActiveActivitiesFilters({ typeFilter: filter, contextFilter });
  const emptyText = getActivitiesEmptyText({ typeFilter: filter, contextFilter });
  const canDelete = canDeleteActivity({ role, isSuperAdmin });

  const usersById = useMemo(() => {
    const map = {};
    for (const u of users || []) {
      if (u?.id) map[u.id] = u;
    }
    return map;
  }, [users]);

  useEffect(() => {
    api
      .get("/api/settings/users")
      .then((r) => setUsers(Array.isArray(r.data) ? r.data : []))
      .catch(() => setUsers([]));
  }, []);

  const load = useCallback(async (cursor = null, append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const params = { limit: GLOBAL_ACTIVITY_PAGE_SIZE };
      if (filter) params.type = filter;
      if (contextFilter !== ALL_CONTEXTS) params.context = contextFilter;
      if (cursor?.created_at && cursor?.id) {
        params.before_created_at = cursor.created_at;
        params.before_id = cursor.id;
      }
      const r = await api.get("/api/activities/page", { params });
      const page = normalizeActivityPageResponse(r.data);
      setItems((prev) => (append ? mergeActivities(prev, page.items) : page.items));
      setHasMore(page.hasMore);
      setNextCursor(page.nextCursor);
    } catch (e) {
      toast.error(extractApiError(e, "Aktivitäten konnten nicht geladen werden."));
      if (!append) {
        setItems([]);
        setHasMore(false);
        setNextCursor(null);
      }
    } finally {
      if (append) setLoadingMore(false);
      else setLoading(false);
    }
  }, [contextFilter, filter]);

  useEffect(() => {
    setItems([]);
    setHasMore(false);
    setNextCursor(null);
    load();
  }, [load]);

  const resetFilters = () => {
    if (!hasActiveFilters) return;
    setFilter(null);
    setContextFilter(ALL_CONTEXTS);
    setItems([]);
    setHasMore(false);
    setNextCursor(null);
  };

  const handleDeleteActivity = useCallback(async (activity) => {
    try {
      await api.delete(`/api/activities/${activity.id}`);
      setItems((prev) => prev.filter((item) => item.id !== activity.id));
      toast.success("Aktivität gelöscht.");
    } catch (e) {
      const msg = extractApiError(e, "Aktivität konnte nicht gelöscht werden.");
      toast.error(msg);
      throw e;
    }
  }, []);

  return (
    <div>
      <PageHeader title="Aktivitäten" description="Chronologische Aktivitäten für Ihren Mandanten." />

      <div className="surface rounded-xl p-3 mb-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs font-medium text-muted-foreground">Typ</div>
          {hasActiveFilters ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={resetFilters}
              disabled={loading || loadingMore}
              data-testid="activity-filters-reset"
            >
              Filter zurücksetzen
            </Button>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2">
          <FilterChip active={filter === null} onClick={() => setFilter(null)} testId="activity-filter-all">
            Alle
          </FilterChip>
          {Object.values(ACTIVITY_TYPE).map((t) => (
            <FilterChip
              key={t}
              active={filter === t}
              onClick={() => setFilter(t)}
              testId={`activity-filter-${t}`}
            >
              {ACTIVITY_TYPE_LABELS[t]?.de || t}
            </FilterChip>
          ))}
        </div>

        <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3">
          <div className="text-xs font-medium text-muted-foreground">Kontext</div>
          <Select value={contextFilter} onValueChange={setContextFilter}>
            <SelectTrigger className="w-full sm:w-56" data-testid="activity-context-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACTIVITY_CONTEXT_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="surface rounded-xl p-5">
        {loading ? (
          <div className="text-sm text-muted-foreground py-4 text-center">Wird geladen …</div>
        ) : (
          <>
            <Timeline
              items={items}
              usersById={usersById}
              emptyText={emptyText}
              canDelete={canDelete}
              onDelete={handleDeleteActivity}
            />
            <div className="mt-4 flex flex-col items-center gap-2">
              {hasMore ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => load(nextCursor, true)}
                  disabled={loadingMore}
                >
                  {loadingMore ? "Weitere Aktivitäten werden geladen …" : "Mehr Aktivitäten laden"}
                </Button>
              ) : items.length > 0 ? (
                <div className="text-xs text-muted-foreground">Alle Aktivitäten geladen</div>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={
        "px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-150 btn-tactile " +
        (active
          ? "border-transparent text-primary-foreground brand-glow"
          : "border-border bg-card text-foreground/85 hover:bg-muted hover:text-foreground")
      }
      style={active ? { background: "var(--brand)" } : undefined}
    >
      {children}
    </button>
  );
}
